import { Component, DestroyRef, effect, inject, input, OnDestroy, OnInit, output, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { ProductCardWithQuantityComponent } from "@components/product-card-with-quantity/product-card-with-quantity.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { FgdsProductType, FullProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { BehaviorSubject, combineLatest, map, ReplaySubject } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: "app-terminal-blocks-selector",
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UiKitModule,
    FilterProductComponent,
    ProductCardWithQuantityComponent,
    RefreshProductsHintComponent,
  ],
  templateUrl: "./terminal-blocks-selector.component.html",
  styleUrl: "./terminal-blocks-selector.component.scss",
})
export class TerminalBlocksSelectorComponent implements OnInit, OnDestroy {
  private readonly productService = inject(ProductService);
  private readonly destroyRef = inject(DestroyRef);

  readonly project = input.required<Project>();
  readonly originalTerminalBlocks = input<ProductWithQuantity[]>();
  readonly selectedModules = input<{
    inputModules: ProductWithQuantity[];
    relayModules: ProductWithQuantity[];
    gatewayModules: ProductWithQuantity[];
  }>();

  readonly terminalBlocksSelect$ = output<
    {
      product: Product;
      quantity: number;
    }[]
  >();

  protected readonly filteredTerminalBlocks = signal<Product[]>([]);
  protected preselectedTerminalBlocks: ProductWithQuantity[] = [];

  private readonly selectedTerminalBlocks = signal<ProductWithQuantity[]>([]);
  private readonly terminalBlocks$ = new ReplaySubject<Product[]>(1);
  private readonly searchValue$ = new BehaviorSubject<string | null>(null);

  constructor() {
    combineLatest([this.searchValue$, this.terminalBlocks$]).subscribe(([searchValue, terminalBlocks]) => {
      if (searchValue) {
        terminalBlocks = terminalBlocks.filter((product) => this.productService.searchFilter(product, searchValue));
      }
      this.filteredTerminalBlocks.set(terminalBlocks);
    });

    effect(() => {
      const originalTerminalBlocks = this.originalTerminalBlocks();
      if (!originalTerminalBlocks?.length) {
        return;
      }
      this.updatePreselectedTerminalBlocks(originalTerminalBlocks);
    });

    effect(() => {
      const selectedModules = this.selectedModules();
      if (!selectedModules) return;
      this.preselect(selectedModules);
    });
  }
  ngOnInit() {
    this.collectTerminalBlocks();
    this.handleSearchValueChange();
  }

  ngOnDestroy() {
    this.terminalBlocks$.complete();
    this.searchValue$.complete();
  }

  protected search(value: string) {
    this.searchValue$.next(value);
  }

  protected getSelectedQuantity(product: Product) {
    const selected = this.selectedTerminalBlocks().find((terminalBlock) => terminalBlock.product.id === product.id);
    return selected?.quantity ?? 0;
  }

  protected getPreselectedQuantity(product: Product) {
    const preselected = this.preselectedTerminalBlocks.find((terminalBlock) => terminalBlock.product.id === product.id);
    return preselected?.quantity ?? 0;
  }

  protected updateSelectedTerminalBlocks(quantity: number, product: Product) {
    const current = this.selectedTerminalBlocks().find((selected) => selected.product.id === product.id);
    if ((quantity === 0 && !current) || (quantity > 0 && current?.quantity === quantity)) {
      return;
    }
    const updated = this.selectedTerminalBlocks().filter((selected) => selected.product.id !== product.id);
    if (quantity > 0) {
      updated.push({ product: product, quantity });
    }
    this.selectedTerminalBlocks.set(updated);
    this.terminalBlocksSelect$.emit(updated);
  }

  private collectTerminalBlocks() {
    this.productService
      .getAvailableProducts(FgdsProductType.GASWARNINGCENTER)
      .pipe(
        take(1),
        map((products) =>
          products.filter((p) =>
            [
              FullProductType.GASWARNINGCENTER_TB,
              FullProductType.GASWARNINGCENTER_TB_BUS_INPUT,
              FullProductType.GASWARNINGCENTER_TB_DIGITAL_INPUT,
              FullProductType.GASWARNINGCENTER_TB_RELAYS_240V,
              FullProductType.GASWARNINGCENTER_TB_ANALOG_INPUT_RELAYS_24V,
            ].includes(p.fullType),
          ),
        ),
      )
      .subscribe((terminalBlocks) => {
        this.terminalBlocks$.next(terminalBlocks);
      });
  }

  private handleSearchValueChange() {
    combineLatest([this.searchValue$, this.terminalBlocks$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([searchValue, terminalBlocks]) => {
        if (!terminalBlocks) {
          this.filteredTerminalBlocks.set([]);
          return;
        }
        if (!searchValue) {
          this.filteredTerminalBlocks.set(terminalBlocks);
          return;
        }
        this.filteredTerminalBlocks.set(
          terminalBlocks.filter((product) => this.productService.searchFilter(product, searchValue)),
        );
      });
  }

  private updatePreselectedTerminalBlocks(originalTerminalBlocks: ProductWithQuantity[]) {
    this.terminalBlocks$.pipe(take(1)).subscribe((terminalBlocks) => {
      this.preselectedTerminalBlocks = originalTerminalBlocks
        .map((original) => {
          const matchingProduct = terminalBlocks.find((product) => product.id === original.product.id);
          return matchingProduct ? { product: matchingProduct, quantity: original.quantity } : null;
        })
        .filter((item): item is ProductWithQuantity => item !== null && item.product.isAvailable);
    });
  }

  private preselect(selectedModules: {
    inputModules: ProductWithQuantity[];
    relayModules: ProductWithQuantity[];
    gatewayModules: ProductWithQuantity[];
  }) {
    this.terminalBlocks$.pipe(take(1)).subscribe((terminalBlocks) => {
      if (terminalBlocks.length > 1) {
        const preselected: ProductWithQuantity[] = [];
        const getQuantity = (selectedModules: ProductWithQuantity[]) =>
          Object.values(selectedModules)
            .flat()
            .reduce((sum, { quantity }) => sum + quantity, 0);

        const digitalInputs = selectedModules.inputModules.filter(
          ({ product }) => product.fullType == FullProductType.GASWARNINGCENTER_INPUT_DIGITAL,
        );
        const tbDigital = terminalBlocks.find(({ fullType }) => fullType == FullProductType.GASWARNINGCENTER_TB_DIGITAL_INPUT);
        if (digitalInputs.length && tbDigital) {
          preselected.push({ product: tbDigital, quantity: getQuantity(digitalInputs) });
        }

        const busInputs = selectedModules.inputModules.filter(
          ({ product }) => product.fullType == FullProductType.GASWARNINGCENTER_INPUT_BUS,
        );
        const tbBus = terminalBlocks.find(({ fullType }) => fullType == FullProductType.GASWARNINGCENTER_TB_BUS_INPUT);
        if (busInputs.length && tbBus) {
          preselected.push({ product: tbBus, quantity: getQuantity(busInputs) });
        }

        const analogInputs = selectedModules.inputModules.filter(
          ({ product }) => product.fullType == FullProductType.GASWARNINGCENTER_INPUT_ANALOG,
        );
        const relays24V = selectedModules.relayModules.filter(
          ({ product }) => product.fullType == FullProductType.GASWARNINGCENTER_RELAYS_24V,
        );
        const tbAnalogRelay24V = terminalBlocks.find(
          ({ fullType }) => fullType == FullProductType.GASWARNINGCENTER_TB_ANALOG_INPUT_RELAYS_24V,
        );
        if ((analogInputs.length || relays24V.length) && tbAnalogRelay24V) {
          const quantity = getQuantity(analogInputs) + getQuantity(relays24V);
          preselected.push({ product: tbAnalogRelay24V, quantity });
        }

        const relays240V = selectedModules.relayModules.filter(
          ({ product }) => product.fullType == FullProductType.GASWARNINGCENTER_RELAYS_240V,
        );
        const tbRelay240V = terminalBlocks.find(({ fullType }) => fullType == FullProductType.GASWARNINGCENTER_TB_RELAYS_240V);
        if (relays240V.length && tbRelay240V) {
          preselected.push({ product: tbRelay240V, quantity: getQuantity(relays240V) });
        }

        this.selectedTerminalBlocks.set(preselected);
        this.terminalBlocksSelect$.emit(preselected);
      }
    });
  }
}
